import { action, observable, makeObservable } from 'mobx';
import { Model, IModelAttributes, attribute, entity } from 'Models/Model';
import * as Models from 'Models/Entities';
import { isNotNullOrUndefined } from 'Util/TypeGuards';

export interface IUserDisplayNameEntityAttributes extends IModelAttributes {
	displayName: string;
	description: string;
	invoicedUser: boolean;
	tagColour: string;

	defaultPricingId?: string;
	defaultPricing?: Models.DefaultPricingEntity | Models.IDefaultPricingEntityAttributes;
	users: Array<Models.UserEntity | Models.IUserEntityAttributes>;
	companyNameFieldEnabled: boolean;
	companyNameFieldRequired: boolean;
	additionalInformationLabelKey?: string;
	additionalInformationPlaceholderKey?: string;
	additionalInformationEnabled: boolean;
	additionalInformationRequired: boolean;
	disableReminderEmails: boolean;
	disableConfirmationEmails: boolean;
	disableThankYouEmails: boolean;
	vehicleTermsAndConditions?: string;
	passengerTermsAndConditions?: string;
	freightTermsAndConditions?: string;
	availablePaymentOptions?: string;
	requestable?: boolean;
	bookingCutoffs: Array<Models.BookingCutoffsUserDisplayNames | Models.IBookingCutoffsUserDisplayNamesAttributes>;
}
@entity('UserDisplayNameEntity', 'User Display Name')
export default class UserDisplayNameEntity extends Model implements IUserDisplayNameEntityAttributes {
	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	/**
	 * The display name that will appear throughout the website.
	 */
	@observable
	@attribute<UserDisplayNameEntity, string>()
	public displayName: string;
	/**
	 * The description of what this user type should be used for.
	 */

	@observable
	@attribute<UserDisplayNameEntity, string>()
	public description: string;
	/**
	 * Whether the user is invoiced or not.
	 */

	@observable
	@attribute<UserDisplayNameEntity, boolean>()
	public invoicedUser: boolean;

	/**
	 * The colour that the user type tag will appear throughout the application.
	 */
	@observable
	@attribute<UserDisplayNameEntity, string>()
	public tagColour: string;

	@observable
	@attribute()
	public defaultPricingId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public defaultPricing: Models.DefaultPricingEntity;

	@observable
	@attribute({ isReference: true, manyReference: true })
	public users: Models.UserEntity[] = [];

	@observable
	@attribute<UserDisplayNameEntity, boolean>()
	public companyNameFieldEnabled: boolean;

	@observable
	@attribute<UserDisplayNameEntity, boolean>()
	public companyNameFieldRequired: boolean;

	@observable
	@attribute<UserDisplayNameEntity, string>()
	public additionalInformationLabelKey: string;

	@observable
	@attribute<UserDisplayNameEntity, string>()
	public additionalInformationPlaceholderKey: string;

	@observable
	@attribute<UserDisplayNameEntity, boolean>()
	public additionalInformationEnabled: boolean;

	@observable
	@attribute<UserDisplayNameEntity, boolean>()
	public additionalInformationRequired: boolean;

	@observable
	@attribute<UserDisplayNameEntity, boolean>()
	public disableReminderEmails: boolean;

	@observable
	@attribute<UserDisplayNameEntity, boolean>()
	public disableConfirmationEmails: boolean;

	@observable
	@attribute<UserDisplayNameEntity, boolean>()
	public disableThankYouEmails: boolean;

	@observable
	@attribute<UserDisplayNameEntity, string>()
	public vehicleTermsAndConditions?: string;

	@observable
	@attribute<UserDisplayNameEntity, string>()
	public passengerTermsAndConditions?: string;

	@observable
	@attribute<UserDisplayNameEntity, string>()
	public freightTermsAndConditions?: string;

	/**
	 * CSV of PaymentOption tokens (e.g. "CreditCard,Invoice") this customer type may
	 * choose at checkout. Drives per-type wizard payment gating.
	 */
	@observable
	@attribute<UserDisplayNameEntity, string>()
	public availablePaymentOptions?: string;

	/**
	 * Whether customers (or staff on their behalf) can request this customer type from the
	 * account page.
	 */
	@observable
	@attribute<UserDisplayNameEntity, boolean>()
	public requestable?: boolean;

	@observable
	@attribute({ isReference: true, manyReference: true })
	public bookingCutoffs: Models.BookingCutoffsUserDisplayNames[] = [];

	constructor(attributes?: Partial<IUserDisplayNameEntityAttributes>) {
		super(attributes);

		this.users = [];
		this.bookingCutoffs = [];

		makeObservable(this);

		this.assignAttributes(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	public assignAttributes(attributes?: Partial<IUserDisplayNameEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.displayName !== undefined) {
				this.displayName = attributes.displayName;
			}
			if (attributes.description !== undefined) {
				this.description = attributes.description;
			}
			if (attributes.invoicedUser !== undefined) {
				this.invoicedUser = attributes.invoicedUser;
			}
			if (attributes.tagColour !== undefined) {
				this.tagColour = attributes.tagColour;
			}
			if (attributes.defaultPricingId !== undefined) {
				this.defaultPricingId = attributes.defaultPricingId;
			}
			if (attributes.defaultPricing !== undefined) {
				if (attributes.defaultPricing === null) {
					this.defaultPricing = attributes.defaultPricing;
				} else if (attributes.defaultPricing instanceof Models.DefaultPricingEntity) {
					this.defaultPricing = attributes.defaultPricing;
					this.defaultPricingId = attributes.defaultPricing.id;
				} else {
					this.defaultPricing = new Models.DefaultPricingEntity(attributes.defaultPricing);
					this.defaultPricingId = this.defaultPricing.id;
				}
			}
			if (attributes.users !== undefined && Array.isArray(attributes.users)) {
				if (!this.users) {
					this.users = [];
				}
				for (const model of attributes.users) {
					if (model instanceof Models.UserEntity) {
						this.users.push(model);
					} else {
						this.users.push(new Models.UserEntity(model));
					}
				}
			}
			if (isNotNullOrUndefined(attributes.companyNameFieldEnabled)) {
				this.companyNameFieldEnabled = attributes.companyNameFieldEnabled;
			}
			if (isNotNullOrUndefined(attributes.companyNameFieldRequired)) {
				this.companyNameFieldRequired = attributes.companyNameFieldRequired;
			}
			if (isNotNullOrUndefined(attributes.additionalInformationLabelKey)) {
				this.additionalInformationLabelKey = attributes.additionalInformationLabelKey;
			}
			if (isNotNullOrUndefined(attributes.additionalInformationPlaceholderKey)) {
				this.additionalInformationPlaceholderKey = attributes.additionalInformationPlaceholderKey;
			}
			if (isNotNullOrUndefined(attributes.additionalInformationEnabled)) {
				this.additionalInformationEnabled = attributes.additionalInformationEnabled;
			}
			if (isNotNullOrUndefined(attributes.additionalInformationRequired)) {
				this.additionalInformationRequired = attributes.additionalInformationRequired;
			}
			if (isNotNullOrUndefined(attributes.disableReminderEmails)) {
				this.disableReminderEmails = attributes.disableReminderEmails;
			}
			if (isNotNullOrUndefined(attributes.disableConfirmationEmails)) {
				this.disableConfirmationEmails = attributes.disableConfirmationEmails;
			}
			if (isNotNullOrUndefined(attributes.disableThankYouEmails)) {
				this.disableThankYouEmails = attributes.disableThankYouEmails;
			}
			if (attributes.vehicleTermsAndConditions !== undefined) {
				this.vehicleTermsAndConditions = attributes.vehicleTermsAndConditions;
			}
			if (attributes.passengerTermsAndConditions !== undefined) {
				this.passengerTermsAndConditions = attributes.passengerTermsAndConditions;
			}
			if (attributes.freightTermsAndConditions !== undefined) {
				this.freightTermsAndConditions = attributes.freightTermsAndConditions;
			}
			if (attributes.availablePaymentOptions !== undefined) {
				this.availablePaymentOptions = attributes.availablePaymentOptions;
			}
			if (isNotNullOrUndefined(attributes.requestable)) {
				this.requestable = attributes.requestable;
			}
			if (attributes.bookingCutoffs !== undefined && Array.isArray(attributes.bookingCutoffs)) {
				if (!this.bookingCutoffs) {
					this.bookingCutoffs = [];
				}
				for (const model of attributes.bookingCutoffs) {
					if (model instanceof Models.BookingCutoffsUserDisplayNames) {
						this.bookingCutoffs.push(model);
					} else {
						this.bookingCutoffs.push(new Models.BookingCutoffsUserDisplayNames(model));
					}
				}
			}
		}
	}
}
